import { memo, useEffect, useRef } from 'react';
import { cn } from '@shared/lib/utils';

import type { ShowInfoByIdResponse } from 'src/modules/dateSelection/models/show.interface';

interface DateCarouselProps {
  showInfo: ShowInfoByIdResponse;
  onDateSelect: (e: React.MouseEvent, eventId: number) => void;
  selectedDate: {
    date: string;
    time: string;
    eventId: number;
  } | null;
}
export const DateCarousel = memo(({ showInfo, onDateSelect, selectedDate }: DateCarouselProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedCardRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to selected date on mount or when selectedDate changes
  useEffect(() => {
    if (!selectedDate?.eventId || !containerRef.current || !selectedCardRef.current) return;

    const scrollToSelected = () => {
      const container = containerRef.current;
      const selectedCard = selectedCardRef.current;
      if (!container || !selectedCard) return;

      const containerWidth = container.offsetWidth;
      const cardLeft = selectedCard.offsetLeft;
      const cardWidth = selectedCard.offsetWidth;
      const scrollLeft = cardLeft - containerWidth / 2 + cardWidth / 2;

      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
    };

    // Initial scroll
    scrollToSelected();

    // Add a small delay to ensure all elements are rendered
    const timeoutId = setTimeout(scrollToSelected, 100);

    return () => clearTimeout(timeoutId);
  }, [selectedDate?.eventId]);

  return (
    <div className="relative mb-3 flex items-center">
      <button
        onClick={() => {
          const container = containerRef.current;
          if (container) container.scrollBy({ left: -140, behavior: 'smooth' });
        }}
        className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[color:var(--surface-elevated)] shadow-[var(--shadow-md)] hover:bg-surface-card md:inline-flex"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="none"
          className="w-4 h-4"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m15 18-6-6 6-6" />
        </svg>
      </button>

      <div
        ref={containerRef}
        className="flex-1 overflow-x-auto scroll-smooth scrollbar-hide px-1 py-1 md:mx-2 md:px-2 [scroll-snap-type:x_proximity]"
        style={{ overscrollBehaviorX: 'contain', touchAction: 'pan-x' }}
      >
        <div className="flex w-max">
          {showInfo?.events
            ?.slice()
            ?.sort((a, b) => new Date(a.from).getTime() - new Date(b.from).getTime())
            ?.map((item, index) => (
              <div
                key={index}
                ref={selectedDate?.eventId === item.id ? selectedCardRef : null}
                className="shrink-0 [scroll-snap-align:start]"
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDateSelect(e, item.id);
                  }}
                  className={cn(
                    'relative w-[130px] h-[38px] px-3 flex items-center justify-center cursor-pointer select-none',
                    'transition-colors',
                    selectedDate?.eventId === item.id
                      ? 'text-action-primary'
                      : 'text-text-secondary hover:text-text-primary'
                  )}
                >
                  <span
                    aria-hidden
                    className="absolute -left-[2px] top-0 h-full w-[2px] rounded-sm bg-border-strong"
                  />
                  <div className="flex flex-col items-center leading-tight">
                    <span className="text-sm whitespace-nowrap">
                      {new Date(item.from).toLocaleDateString('ru-RU', {
                        day: 'numeric',
                        month: 'long',
                      })}
                    </span>
                    <span
                      className={cn(
                        'text-[12px] whitespace-nowrap',
                        selectedDate?.eventId === item.id ? 'text-action-primary' : 'text-text-muted'
                      )}
                    >
                      {new Date(item.from).toLocaleTimeString('ru-RU', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                </div>
              </div>
            )) || []}
        </div>
      </div>

      <button
        onClick={() => {
          const container = containerRef.current;
          if (container) container.scrollBy({ left: 140, behavior: 'smooth' });
        }}
        className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[color:var(--surface-elevated)] shadow-[var(--shadow-md)] hover:bg-surface-card md:inline-flex"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          className="w-4 h-4"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
      </button>
    </div>
  );
});
